import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, InputAdornment, Box, Typography,
  Checkbox, FormControlLabel, useMediaQuery, IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SearchRounded as SearchIcon, CloseRounded as CloseIcon } from '@mui/icons-material';
import { tokens } from '../theme';

// Choose the bookmakers the user holds accounts with. An empty selection means "all".
export default function BookmakerPicker({ open, bookmakers, selected, onClose, onSave, saving }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(new Set());

  // Start from the saved selection each time the dialog opens ("all" shows every box
  // ticked). Only on open: re-running on prop changes would wipe the user's ticks.
  useEffect(() => {
    if (open) {
      setPicked(new Set(selected?.length ? selected : bookmakers.map(b => b.key)));
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookmakers.filter(b => !q || b.title.toLowerCase().includes(q));
  }, [bookmakers, query]);

  const toggle = (key) => setPicked(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const allPicked = bookmakers.length > 0 && bookmakers.every(b => picked.has(b.key));
  const tooFew = picked.size < 1;

  const save = () => onSave(allPicked ? [] : [...picked]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="xs" fullWidth
      PaperProps={{ sx: { bgcolor: tokens.surface, ...(fullScreen ? {} : { height: '80vh' }) } }}>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        My bookmakers
        <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Only bookmakers you can bet with are used to build arbs. Pick one to see where it loses least.
        </Typography>
      </DialogTitle>
      <Box px={3} pb={1.5}>
        <TextField
          size="small" fullWidth placeholder="Search bookmakers" value={query} onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="caption" color="text.secondary">{picked.size} of {bookmakers.length} selected</Typography>
          <Box>
            <Button size="small" onClick={() => setPicked(new Set(bookmakers.map(b => b.key)))}>All</Button>
            <Button size="small" onClick={() => setPicked(new Set())}>None</Button>
          </Box>
        </Box>
      </Box>
      <DialogContent dividers sx={{ px: 1.5, py: 0.5, borderColor: tokens.border }}>
        {visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>No bookmakers match "{query}"</Typography>
        ) : visible.map(b => (
          <FormControlLabel
            key={b.key}
            control={<Checkbox checked={picked.has(b.key)} onChange={() => toggle(b.key)} />}
            label={b.title}
            sx={{ display: 'flex', mx: 0, px: 0.5, borderRadius: 2, '&:hover': { bgcolor: tokens.surfaceRaised } }}
          />
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
        {tooFew && (
          <Typography variant="caption" color="warning.main" textAlign="center">
            Pick at least one bookmaker.
          </Typography>
        )}
        <Box display="flex" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={save} disabled={tooFew || saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
